import { useState } from 'react'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useTranslation } from 'react-i18next'
import { dateToString } from 'utils/addContract'
import { AddContractStepProps, AddContractSteps } from '../../types'
import contractsStyles from '../../../Contracts.module.scss'
import FormButtons from 'features/contracts/components/form-buttons/FormButtons'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { fetchContactData, formatAddress } from 'utils/user'
import DataBlock from 'features/contracts/add/components/data-block/DataBlock.tsx'
import dayjs from 'dayjs'
import { checkHasMeterAndNumberInputs } from 'utils/contracts.ts'
import { setMoveFlowState } from 'store/app/slice.ts'
import { ContractFlowTrackingTypes } from 'types/tracking.ts'
import { Banner, Card, Checkbox } from '@boltenergy-be/design-system'
import { UserTypes } from 'store/auth/types.ts'
import { AddressFormats } from 'types/format.ts'
import { trackEvent } from 'utils/tracking.ts'
import backendLib from 'lib/bolt-backend'
import {
  CONTACT_MODES,
  COUNTER_TYPES,
  ERROR_CODES,
  LANGUAGES,
  METER_TYPES,
  PAYMENT_METHODS,
  ROLES,
  SETTLEMENT_FREQUENCIES,
  SITUATIONS
} from '@boltenergy-be/shared'
import { DATE_FORMAT } from 'constants/constants.ts'
import { useGetProductsQuery } from 'store/queries/bolt-api/products'
import { IS_PROD_TESTING } from 'constants/envs.ts'

const OverviewStep = ({ addContractData, isMove, setCurrentStep }: AddContractStepProps) => {
  // Redux
  const { move } = useStoreSelector((store) => store.app)
  const { userType, role } = useStoreSelector((store) => store.auth)
  const dispatch = useStoreDispatch()

  // Redux query
  const { data: productsData, isLoading: isLoadingProductsData } = useGetProductsQuery()

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [agreesWithTermsAndConditions, setAgreesWithTermsAndConditions] = useState<boolean>(false)

  // i18n
  const { t } = useTranslation(['contracts', 'common', 'errors'])

  // Constants
  const { customerData, meterDetails, address } = addContractData
  const { deliveryAddress } = address
  const canBypassCreditCheck = role === ROLES.operatorSenior && error === ERROR_CODES.badGraydonCreditCheck
  const { correspondenceAddress } = customerData
  const showMeterReadAndNumbers = checkHasMeterAndNumberInputs({
    situation: meterDetails.situation,
    counterType: meterDetails.counterType,
    contractStartDate: meterDetails.contractStartDate
  })

  /**
   * Handles the submit after validation by React Hook Form
   */
  const onSubmit = async () => {
    setError(null)
    setLoading(true)

    if (!IS_PROD_TESTING && agreesWithTermsAndConditions) {
      if (!productsData) {
        throw new Error('No products data for pricelists')
      }

      try {
        // create contract
        const { success, message, errorCode } = await backendLib.createContract(
          addContractData,
          productsData.products,
          canBypassCreditCheck
        )

        if (success) {
          // Re-fetch the contact data
          if (userType && userType !== UserTypes.SALES) {
            await fetchContactData(userType, customerData.email)
          }

          // Track the event
          trackEvent({ category: 'contract', object: 'add_contract', action: 'finish' }, { flow: move.flow })

          // Add the move flow state to the store
          dispatch(
            setMoveFlowState({
              key: 'started',
              state: {
                address: deliveryAddress,
                date: meterDetails.contractStartDate
              }
            })
          )

          // Go to the "done" step
          setCurrentStep(AddContractSteps.DONE)
        } else {
          setError(errorCode || message || error)
        }
      } catch (err) {
        const error = err as Error
        setError(error.message ?? 'error')
      }
    }

    setLoading(false)
  }

  /**
   * Returns the error text based on the error code
   *
   * @param {string} err
   * @returns {string}
   */
  const getErrorText = (err: string): string => {
    switch (err) {
      case ERROR_CODES.notACustomer:
        return t(($) => $.bolt.notACustomer, {
          ns: 'errors'
        })

      case ERROR_CODES.requestedProducerFull:
        return t(($) => $.bolt.requestedProducerFull, {
          ns: 'errors'
        })

      case ERROR_CODES.eanAlreadyInPortfolio:
        return t(($) => $.bolt.customerAlreadyHasEanCodes, {
          count: meterDetails?.gas?.ean ? 2 : 1,
          ns: 'errors'
        })

      case ERROR_CODES.badGraydonCreditCheck:
        return t(($) => $.bolt.graydonCreditCheckFailed, {
          context: role === ROLES.operatorSenior ? 'bypass' : undefined,
          ns: 'errors'
        })

      default:
        return t(($) => $.default, {
          ns: 'errors'
        })
    }
  }

  return (
    <form className={contractsStyles['form-card']} onSubmit={onSubmit}>
      <div className={contractsStyles['form-content']}>
        <Card.Title>{t(($) => $.add.steps.confirmation.title)}</Card.Title>

        <div className="form-group">
          <h3 className={contractsStyles['label-large']}>{t(($) => $.add.steps.confirmation.subTitle)}</h3>
          <div className={contractsStyles.description}>{t(($) => $.add.steps.confirmation.description)}</div>
        </div>

        {/* PERSONAL DATA */}
        <section className={contractsStyles['overview-section']}>
          <h3 className={contractsStyles['section-title']}>{t(($) => $.add.steps.confirmation.personalData.title)}</h3>
          <DataBlock label={t(($) => $.add.steps.confirmation.personalData.fields.fuelType)}>
            {t(($) => $.add.steps.address.form.fuelType.labels[address.needsGas ? 'elecAndGas' : 'onlyElec'])}
          </DataBlock>

          <DataBlock label={t(($) => $.add.steps.confirmation.personalData.fields.deliveryAddress)}>
            {parse(formatAddress(deliveryAddress, AddressFormats.FULL_TWO_LINES) || '')}
          </DataBlock>

          {customerData.isCompany && customerData.company && (
            <DataBlock label={t(($) => $.add.steps.confirmation.personalData.fields.company)}>
              <span>
                {customerData.company.name} {t(($) => $.legalForms[customerData.company!.legalForm], { ns: 'common' })}
              </span>
              <span>
                {t(($) => $.add.steps.customerData.fields.companyNumber)}: {customerData.company.enterpriseNumber}
              </span>
              {customerData.company.vatApplication && <span>{t(($) => $.add.steps.customerData.fields.subjectToVat)}</span>}
            </DataBlock>
          )}

          <DataBlock
            label={t(($) => $.add.steps.confirmation.contactDetails.title, {
              defaultValue: 'Contactgegevens'
            })}
          >
            <span>
              {customerData.firstName} {customerData.lastName}
            </span>
            <span>{customerData.email}</span>
            <span>{customerData.phoneMobile}</span>
          </DataBlock>

          {customerData.birthDate && (
            <DataBlock label={t(($) => $.add.steps.customerData.fields.birthDate.label)}>
              {dayjs(customerData.birthDate).format(DATE_FORMAT)}
            </DataBlock>
          )}

          <DataBlock label={t(($) => $.add.steps.customerData.fields.language.label)}>
            {customerData.language === LANGUAGES.NL
              ? t(($) => $.add.steps.customerData.fields.language.dutch)
              : t(($) => $.add.steps.customerData.fields.language.french)}
          </DataBlock>
        </section>

        {/* METER DETAILS */}
        <section className={contractsStyles['overview-section']}>
          <h3 className={contractsStyles['section-title']}>{t(($) => $.add.steps.confirmation.meterDetails.title)}</h3>

          {!isMove && (
            <DataBlock label={t(($) => $.add.steps.confirmation.meterDetails.fields.mySituation)}>
              {t(($) => $.add.steps.meterDetails.form.situation.options[meterDetails.situation])}
            </DataBlock>
          )}

          {meterDetails.docUpload && (
            <DataBlock label={t(($) => $.add.steps.meterDetails.form.docUpload)}>{meterDetails.docUpload[0].name}</DataBlock>
          )}

          <DataBlock label={t(($) => $.add.steps.confirmation.meterDetails.fields.solarPanels)}>
            {meterDetails.hasSolarPanels
              ? t(($) => $.yes, {
                  ns: 'common'
                })
              : t(($) => $.no, {
                  ns: 'common'
                })}
          </DataBlock>

          <DataBlock label={t(($) => $.add.steps.confirmation.meterDetails.fields.counterType)}>
            {meterDetails.counterType === COUNTER_TYPES.digital
              ? t(($) => $.yes, {
                  ns: 'common'
                })
              : t(($) => $.no, {
                  ns: 'common'
                })}
          </DataBlock>

          {/* - Meter reading month */}
          {meterDetails.situation === SITUATIONS.switch && meterDetails.meterReadingMonth && meterDetails.hasSolarPanels && (
            <DataBlock label={t(($) => $.add.steps.confirmation.meterDetails.fields.meterReadingMonth)}>
              {dayjs(meterDetails.meterReadingMonth).format('MMMM')}
            </DataBlock>
          )}

          {/* - Electricity */}
          <>
            <DataBlock
              label={t(($) => $.electricity, {
                ns: 'common'
              })}
            >
              <span>EAN: {meterDetails.electricity.ean}</span>
              {showMeterReadAndNumbers && (
                <>
                  {meterDetails.electricity.meterNumber && (
                    <span>
                      {t(($) => $.add.steps.meterDetails.form.electricity.meterNumber)}: {meterDetails.electricity.meterNumber}
                    </span>
                  )}

                  {meterDetails.meterType === METER_TYPES.single && meterDetails.electricity.total && (
                    <span>
                      {t(($) => $.add.steps.meterDetails.form.electricity.total)}: {meterDetails.electricity.total}
                    </span>
                  )}

                  {meterDetails.meterType === METER_TYPES.double && (
                    <>
                      {meterDetails.electricity.day && (
                        <span>
                          {t(($) => $.add.steps.meterDetails.form.electricity.day)}: {meterDetails.electricity.day}
                        </span>
                      )}

                      {meterDetails.electricity.night && (
                        <span>
                          {t(($) => $.add.steps.meterDetails.form.electricity.night)}: {meterDetails.electricity.night}
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </DataBlock>

            {meterDetails.hasExclNight && (
              <>
                <DataBlock label={t(($) => $.add.steps.confirmation.meterDetails.fields.meterType.titleExclNight)}>
                  <span>
                    {t(($) => $.add.steps.confirmation.meterDetails.fields.meterReading)}: {meterDetails.electricity.exclNight}
                  </span>
                  {meterDetails.situation === SITUATIONS.move && meterDetails.hasExclNightEan && (
                    <span>EAN: {meterDetails.electricity.eanExclNight}</span>
                  )}
                </DataBlock>
              </>
            )}
          </>

          {/* - Gas */}
          {address.needsGas && meterDetails.gas && (
            <>
              <DataBlock
                label={t(($) => $.gas, {
                  ns: 'common'
                })}
              >
                <span>EAN: {meterDetails.gas.ean}</span>
                {showMeterReadAndNumbers && (
                  <>
                    {meterDetails.gas.meterNumber && (
                      <span>
                        {t(($) => $.add.steps.confirmation.meterDetails.fields.meterNumber)}: {meterDetails.gas.meterNumber}
                      </span>
                    )}

                    {meterDetails.gas.total && (
                      <span>
                        {t(($) => $.add.steps.confirmation.meterDetails.fields.meterReading)}: {meterDetails.gas.total}
                      </span>
                    )}
                  </>
                )}
              </DataBlock>
            </>
          )}

          {/* - Contract start date */}
          {meterDetails.situation !== SITUATIONS.newConnection && meterDetails.contractStartDate && (
            <DataBlock
              label={t(($) => $.add.steps.confirmation.meterDetails.fields.confirmationStartDateContract, {
                defaultValue: 'Startdatum contract'
              })}
            >
              {dateToString(meterDetails.contractStartDate)}
            </DataBlock>
          )}

          {/* Settlement frequency */}
          {meterDetails.settlementFrequency === SETTLEMENT_FREQUENCIES.monthly && (
            <DataBlock label={t(($) => $.add.steps.confirmation.meterDetails.fields.monthlySettlement)}>
              {t(($) => $.yes, {
                ns: 'common'
              })}
            </DataBlock>
          )}

          {/* Dynamic tariff */}
          {meterDetails.dynamicTariff && (
            <DataBlock label={t(($) => $.add.steps.confirmation.meterDetails.fields.dynamicTariff)}>
              {t(($) => $.yes, {
                ns: 'common'
              })}
            </DataBlock>
          )}
        </section>

        {/* BILLING DATA */}
        <section className={contractsStyles['overview-section']}>
          <h3 className={contractsStyles['section-title']}>{t(($) => $.add.steps.confirmation.billingData.title)}</h3>

          <DataBlock label={t(($) => $.add.steps.confirmation.billingData.fields.deliveryMode)}>
            {customerData.contactMode === CONTACT_MODES.email
              ? t(($) => $.add.steps.customerData.fields.deliveryMode.digital)
              : t(($) => $.add.steps.customerData.fields.deliveryMode.paper)}
          </DataBlock>

          <DataBlock label={t(($) => $.add.steps.confirmation.sections.correspondenceAddress)}>
            {parse(formatAddress(correspondenceAddress ?? deliveryAddress, AddressFormats.FULL_TWO_LINES) ?? '')}
          </DataBlock>

          <DataBlock label={t(($) => $.add.steps.confirmation.billingData.fields.paymentMethod)}>
            <span>
              {customerData?.paymentMethod === PAYMENT_METHODS.transfer
                ? t(($) => $.add.steps.customerData.fields.paymentMethod.bankTransfer)
                : t(($) => $.add.steps.customerData.fields.paymentMethod.directDebit)}
            </span>
            {!!customerData?.iban && (
              <span>
                {t(($) => $.add.steps.customerData.fields.iban)}&nbsp;{customerData.iban}
              </span>
            )}
          </DataBlock>
        </section>

        {/* TERMS & CONDITIONS AGREEMENT */}
        <section>
          <Checkbox
            name="agreesWithTermsAndConditions"
            label={<span>{parse(t(($) => $.add.steps.confirmation.terms))}</span>}
            id="agreesWithTermsAndConditions"
            required
            onChange={() => setAgreesWithTermsAndConditions(!agreesWithTermsAndConditions)}
            className={contractsStyles['agree-terms-checkbox']}
          />

          <small className={classNames(contractsStyles.description, 'text-subdued', 'mt-400')}>
            {t(($) => $.add.steps.confirmation.billingLegalInfo)}
          </small>
        </section>

        {error && (
          <div className="row">
            <div className="column form-group">
              <Banner type={canBypassCreditCheck ? 'warning' : 'blocking'} title={getErrorText(error)} />
            </div>
          </div>
        )}
      </div>

      <FormButtons
        trackingId={ContractFlowTrackingTypes.ADD}
        currentStep={AddContractSteps.CONFIRMATION}
        loading={loading || isLoadingProductsData}
        onSubmit={onSubmit}
        submitDisabled={IS_PROD_TESTING || !agreesWithTermsAndConditions || loading || (!!error && !canBypassCreditCheck)}
      />
    </form>
  )
}

export default OverviewStep
